import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  BuildingOffice2Icon,
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import EmptyState from '../ui/EmptyState';
import { searchInputClassName } from '../ui/formStyles';

const SORT_KEYS = {
  name: (company) => company.name || '',
  clients: (company) => company.stats?.clients || 0,
  vendors: (company) => company.stats?.vendors || 0,
  referrals: (company) => company.stats?.referrals || 0,
  revenue: (company) => company.stats?.revenue || 0,
  prospects: (company) => company.stats?.prospects || 0,
  pending_gifts: (company) => company.stats?.pending_gifts || 0,
};

function PlatformCompaniesView({
  companies = [],
  loading,
  loadError = false,
  onRetry,
  variant = 'full',
  onConfigureCompany,
  onViewCompany,
}) {
  const { t } = useLanguage();
  const pa = t.platformAdmin;
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('referrals');
  const [sortDir, setSortDir] = useState('desc');

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    let list = companies;
    if (term) {
      list = list.filter((company) =>
        [company.name, company.slug]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(term))
      );
    }
    return [...list].sort((a, b) => {
      const getter = SORT_KEYS[sortKey] || SORT_KEYS.name;
      const aVal = getter(a);
      const bVal = getter(b);
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [companies, search, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir(key === 'name' ? 'asc' : 'desc');
  };

  const sortIndicator = (key) => {
    if (sortKey !== key) return null;
    return sortDir === 'asc' ? <ArrowUpIcon className="inline h-3 w-3" /> : <ArrowDownIcon className="inline h-3 w-3" />;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="h-28 animate-pulse rounded-2xl bg-secondary-100 " />
        ))}
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="rounded-2xl border border-dashed border-secondary-300 p-10 text-center ">
        <p className="text-secondary-600 ">{pa.overviewLoadError}</p>
        <button
          type="button"
          onClick={() => {
            void onRetry?.();
          }}
          className="mt-4 rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-600"
        >
          {pa.retry}
        </button>
      </div>
    );
  }

  const totals = {
    clients: companies.reduce((sum, company) => sum + (company.stats?.clients || 0), 0),
    referrals: companies.reduce((sum, company) => sum + (company.stats?.referrals || 0), 0),
    revenue: companies.reduce((sum, company) => sum + (company.stats?.revenue || 0), 0),
    pendingGifts: companies.reduce((sum, company) => sum + (company.stats?.pending_gifts || 0), 0),
  };

  return (
    <div className="space-y-6">
      {variant === 'full' ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="card-premium p-4">
            <p className="text-sm text-secondary-500 ">{pa.totalCompanies}</p>
            <p className="text-3xl font-bold text-secondary-900 ">{companies.length}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="card-premium p-4">
            <p className="text-sm text-secondary-500 ">{pa.totalClients}</p>
            <p className="text-3xl font-bold text-success-600">{totals.clients}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="card-premium p-4">
            <p className="text-sm text-secondary-500 ">{pa.platformRevenue}</p>
            <p className="text-3xl font-bold text-emerald-600">${totals.revenue.toLocaleString()}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="card-premium p-4">
            <p className="text-sm text-secondary-500 ">{pa.pendingGifts}</p>
            <p className="text-3xl font-bold text-pink-600">{totals.pendingGifts}</p>
          </motion.div>
        </div>
      ) : null}

      <div className="card-premium overflow-hidden">
        <div className="border-b border-secondary-200/70 p-4 ">
          <div className="relative max-w-md">
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={pa.searchCompanies}
              className={searchInputClassName}
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <EmptyState icon={BuildingOffice2Icon} title={pa.noCompanies} description={pa.noCompaniesDesc} className="py-12" />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-secondary-200 ">
              <thead className="bg-secondary-50 ">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-secondary-500">
                    <button type="button" onClick={() => toggleSort('name')} className="inline-flex items-center gap-1 hover:text-orange-600">{pa.company} {sortIndicator('name')}</button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-secondary-500">{pa.admins}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-secondary-500">
                    <button type="button" onClick={() => toggleSort('vendors')} className="inline-flex items-center gap-1 hover:text-orange-600">{pa.vendorsShort} {sortIndicator('vendors')}</button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-secondary-500">
                    <button type="button" onClick={() => toggleSort('clients')} className="inline-flex items-center gap-1 hover:text-orange-600">{pa.clientsShort} {sortIndicator('clients')}</button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-secondary-500">
                    <button type="button" onClick={() => toggleSort('prospects')} className="inline-flex items-center gap-1 hover:text-orange-600">{pa.prospectsShort} {sortIndicator('prospects')}</button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-secondary-500">
                    <button type="button" onClick={() => toggleSort('referrals')} className="inline-flex items-center gap-1 hover:text-orange-600">{pa.referralsShort} {sortIndicator('referrals')}</button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-secondary-500">
                    <button type="button" onClick={() => toggleSort('revenue')} className="inline-flex items-center gap-1 hover:text-orange-600">{pa.revenue} {sortIndicator('revenue')}</button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-secondary-500">
                    <button type="button" onClick={() => toggleSort('pending_gifts')} className="inline-flex items-center gap-1 hover:text-orange-600">{pa.pendingGifts} {sortIndicator('pending_gifts')}</button>
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-secondary-500">{pa.actions}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-200 bg-white  ">
                {filtered.map((company) => (
                  <tr key={company.id} className="hover:bg-secondary-50/70 ">
                    <td className="px-4 py-4">
                      <button type="button" onClick={() => onViewCompany?.(company.id)} className="flex items-center gap-3 text-left">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 text-sm font-bold text-white">
                          {company.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-secondary-900 ">{company.name}</p>
                          <p className="text-xs text-secondary-500 ">{company.slug}</p>
                        </div>
                      </button>
                    </td>
                    <td className="px-4 py-4 text-sm text-secondary-700 ">{company.stats?.admins || 0}</td>
                    <td className="px-4 py-4 text-sm text-secondary-700 ">{company.stats?.vendors || 0}</td>
                    <td className="px-4 py-4 text-sm text-secondary-700 ">{company.stats?.clients || 0}</td>
                    <td className="px-4 py-4 text-sm text-secondary-700 ">{company.stats?.prospects || 0}</td>
                    <td className="px-4 py-4 text-sm text-secondary-700 ">{company.stats?.referrals || 0}</td>
                    <td className="px-4 py-4 text-sm font-medium text-emerald-600">${(company.stats?.revenue || 0).toLocaleString()}</td>
                    <td className="px-4 py-4 text-sm text-secondary-700 ">{company.stats?.pending_gifts || 0}</td>
                    <td className="px-4 py-4">
                      <div className="flex justify-end gap-2">
                        <button type="button" onClick={() => onViewCompany?.(company.id)} className="rounded-lg border border-secondary-200 px-3 py-1.5 text-xs font-semibold text-secondary-700 transition hover:border-orange-200  ">
                          {pa.viewDetails}
                        </button>
                        <button type="button" onClick={() => onConfigureCompany?.(company.id)} className="rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-orange-600">
                          {pa.configure}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default PlatformCompaniesView;
